import DocViewer, { MSDocRenderer } from "@cyntler/react-doc-viewer";
import debounce from "lodash/debounce";
import { useState, useEffect, useMemo } from "react";
import { generateQuotePreview, getPreSignedUrl } from "../../quoteTemplate/data/quoteTemplateService";
import { useParams } from "react-router-dom";
import { formatNumber } from "../../../utils/numberUtils";

const QuotePreview = ({ quoteItems, groupedQuoteItems, salesForceData, customContent, template }) => {
    const { quoteRevisionId } = useParams();
    const [url, setUrl] = useState("");
    const [status, setStatus] = useState("idle"); // idle | queuing | generating | fetching | ready | error
    const [errorMsg, setErrorMsg] = useState("");

    // Debounce the whole flow so rapid prop changes collapse into a single run
    const debouncedRun = useMemo(
        () =>
            debounce(
                async (payload, signal) => {
                    try {
                        setStatus("generating");
                        await generateQuotePreview(payload);
                        if (signal?.aborted) return;

                        setStatus("fetching");
                        const { quoteRevisionId: qid } = payload;
                        const documentUrl = await getPreSignedUrl(`quotepreview/${qid}.docx`);
                        if (signal?.aborted) return;

                        if (documentUrl?.presignedUrl) {
                            setUrl(documentUrl.presignedUrl);
                            setStatus("ready");
                        } else {
                            throw new Error("No presigned URL returned");
                        }
                    } catch (err) {
                        if (!signal?.aborted) {
                            setErrorMsg(err?.message ?? "Failed to load preview");
                            setStatus("error");
                        }
                    }
                },
                3000,
                { leading: false, trailing: true, maxWait: 10000 }
            ),
        []
    );

    useEffect(() => {
        if (!template || !template?.customSections || !customContent) return;
        if (!quoteItems || quoteItems.length === 0 || !quoteRevisionId || !salesForceData) return;

        let jsonCustomSections = JSON.parse(template?.customSections);
        let jsonCustomSectionsIds = jsonCustomSections.map((a) => a.id);
        let customContentIds = customContent.map((a) => a.id);

        if (!jsonCustomSectionsIds.every((id) => customContentIds.includes(id))) {
            // Required custom sections are not yet available in customContent.
            // Clear any existing preview to avoid showing a stale document.
            setUrl("");
            setStatus("idle");
            return;
        }

        const controller = new AbortController();

        const requestBody = {
            quoteRevisionId,
            quoteItems: groupedQuoteItems,
            salesForceData,
            customContent,
            template,
        };

        setStatus("queuing");
        setErrorMsg("");
        setUrl(""); // clear any previous preview while new one is queued
        debouncedRun(requestBody, controller.signal);


        return () => {
            controller.abort();
            debouncedRun.cancel();
        };
    }, [
        quoteItems,
        groupedQuoteItems,
        salesForceData,
        customContent,
        template,
        quoteRevisionId,
        debouncedRun,
    ]);

    const docs =
        url && template?.s3Key
            ? [{ uri: url, fileType: template.s3Key.split(".").pop(), name: template.s3Key }]
            : [];

    if (!template) {
        return (
            <div style={{ width: "100%", height: "80vh", minHeight: "400px" }}>
                <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
                    <i className="bi bi-file-earmark-text fs-1"></i>
                    <p className="mt-2 mb-0">Please select a template.</p>
                </div>
            </div>
        );
    }

    const EmptyHeader = () => null;

    const Loading = ({ label }) => (
        <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
            <div className="spinner-border" role="status" aria-hidden="true" />
            <p className="mt-3 mb-0">{label}</p>
        </div>
    );

    const ErrorState = ({ message }) => (
        <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center text-danger">
            <i className="bi bi-exclamation-triangle fs-1"></i>
            <p className="mt-2 mb-0">{message || "Something went wrong loading the preview."}</p>
        </div>
    );

    return (
        <div style={{ width: "100%", height: "80vh", minHeight: "400px" }}>
            {status === "error" ? (
                <ErrorState message={errorMsg} />
            ) : docs.length > 0 && status === "ready" ? (
                <div className="doc-wrap">
                    <DocViewer
                        pluginRenderers={[MSDocRenderer]}
                        documents={docs}
                        config={{ header: { overrideComponent: EmptyHeader } }}
                        style={{ height: "100%" }}
                    />
                    <div className="doc-footer-mask" />
                </div>
            ) : (
                <Loading
                    label={
                        status === "queuing"
                            ? "Preparing to generate preview…"
                            : status === "generating"
                                ? "Generating preview…"
                                : status === "fetching"
                                    ? "Fetching document…"
                                    : "Loading…"
                    }
                />
            )}
        </div>
    );
};

export default QuotePreview;
